import { Navigate, useLocation, useParams } from "react-router-dom"
import Dashboard from "../pages/Dashboard"
import WebsiteAudit from "../pages/WebsiteAudit"
import GA4Audit from "../pages/GA4Audit"
import GA4Property from "../pages/GA4Property"
import GA4PropertySettings from "../pages/GA4PropertySettings"
import GA4ConfigurationAudit from "../pages/GA4ConfigurationAudit"
import GA4EventAudit from "../pages/GA4EventAudit"
import GA4HostnameAudit from "../pages/GA4HostnameAudit"
import GA4HostnameDetails from "../pages/GA4HostnameDetails"
import GA4ChannelGroupingAudit from "../pages/GA4ChannelGroupingAudit"
import GA4UnassignedChannelGroup from "../pages/GA4UnassignedChannelGroup"
import GA4SelfReferralAudit from "../pages/GA4SelfReferralAudit"
import GA4PageLocationAudit from "../pages/GA4PageLocationAudit"
import GA4PropertyFCR from "../pages/GA4PropertyFCR"
import GA4PropertyCCR from "../pages/GA4PropertyCCR"
import GA4GoogleAdsAudit from "../pages/GA4GoogleAdsAudit"
import GA4Template from "../pages/GA4Template"
import GA4Access from "../pages/GA4Access"
import PaidMedia from "../pages/PaidMedia"
import AccountAudits from "../pages/AccountAudits"
import GA4UserAccessAudit from "../pages/GA4UserAccessAudit"
import AdminPage from "../pages/AdminPage"
import GTMTagReferenceAdmin from "../pages/GTMTagReferenceAdmin"
import ProductEventOwnerAdmin from "../pages/ProductEventOwnerAdmin"
import ComponentLibrary from "../pages/ComponentLibrary"
import ThemeLibrary from "../pages/ThemeLibrary"
import ManageOrganizations from "../pages/ManageOrganizations"
import OrganizationAdmin from "../pages/OrganizationAdmin"
import OrganizationPlan from "../pages/OrganizationPlan"
import OrganizationCheckout from "../pages/OrganizationCheckout"
import Accounts from "../pages/Accounts"
import Clients from "../pages/Clients"
import ClientDetail from "../pages/ClientDetail"
import Account from "../pages/Account"
import Onboarding from "../pages/Onboarding"
import LoginPage from "../pages/LoginPage"
import MarketingHome from "../pages/marketing/MarketingHome"
import MarketingProduct from "../pages/marketing/MarketingProduct"
import MarketingPricing from "../pages/marketing/MarketingPricing"
import MarketingAbout from "../pages/marketing/MarketingAbout"
import MarketingContact from "../pages/marketing/MarketingContact"
import MarketingAscTuneUp from "../pages/marketing/MarketingAscTuneUp"
import MarketingAscTuneUpFeatures from "../pages/marketing/MarketingAscTuneUpFeatures"
import MarketingAscTuneUpPricing from "../pages/marketing/MarketingAscTuneUpPricing"
import { adminGuard, createAdminGuard, createGa4Guard, requiresOrganizationGuard } from "./guards"

const AccountRootRedirect = () => {
  const { accountId } = useParams()
  return <Navigate to={`/accounts/${accountId}/overview`} replace />
}

const LegacyClientRedirect = () => {
  const { accountId } = useParams()
  return <Navigate to={`/accounts/${accountId}/monitoring`} replace />
}

const ClientsCompatibility = () => {
  const { search } = useLocation()
  return ["monitoring_google", "measurement_documentation"].includes(
    new URLSearchParams(search).get("oauth_provider")
  )
    ? <Clients />
    : <Navigate to="/dashboard" replace />
}

export const createRouteConfigs = ({
  needsOrg,
  selectedOrgId,
  ga4AuditEnabled,
  ascEventUsageEnabled,
  automotiveFeaturesEnabled,
  siteScrapingEnabled,
  paidMediaEnabled,
}) => [
  {
    path: "/",
    guard: { requiresAuth: false },
    component: MarketingHome,
  },
  {
    path: "/product",
    guard: { requiresAuth: false },
    component: MarketingProduct,
  },
  {
    path: "/pricing",
    guard: { requiresAuth: false },
    component: MarketingPricing,
  },
  {
    path: "/about",
    guard: { requiresAuth: false },
    component: MarketingAbout,
  },
  {
    path: "/contact",
    guard: { requiresAuth: false },
    component: MarketingContact,
  },
  {
    path: "/asc-tuneup",
    guard: { requiresAuth: false },
    component: MarketingAscTuneUp,
  },
  {
    path: "/asc-tuneup/features",
    guard: { requiresAuth: false },
    component: MarketingAscTuneUpFeatures,
  },
  {
    path: "/asc-tuneup/pricing",
    guard: { requiresAuth: false },
    component: MarketingAscTuneUpPricing,
  },
  {
    path: "/dashboard",
    component: Dashboard,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/login",
    component: LoginPage,
    guard: {
      requiresAuth: false,
      redirectIfAuthenticated: ({ needsOrg }) =>
        needsOrg ? "/welcome" : "/dashboard",
      redirectUnauthenticated: "/login",
    },
  },
  {
    path: "/welcome",
    component: Onboarding,
    guard: {
      requiresAuth: true,
      requiresOrg: false,
      featureFlag: ({ needsOrg }) => needsOrg,
      redirectFeatureDisabled: "/dashboard",
    },
  },
  {
    path: "/website-audit/*",
    component: WebsiteAudit,
    guard: { ...requiresOrganizationGuard, featureFlag: siteScrapingEnabled },
  },
  {
    path: "/ga4-audit",
    component: GA4Audit,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property",
    component: GA4Property,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/settings",
    component: GA4PropertySettings,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/configuration-audit",
    component: GA4ConfigurationAudit,
    guard: { ...createGa4Guard(ga4AuditEnabled && !automotiveFeaturesEnabled) },
  },
  {
    path: "/ga4-property/event-audit",
    component: GA4EventAudit,
    guard: { ...createGa4Guard(ascEventUsageEnabled) },
  },
  {
    path: "/ga4-property/page-location-audit",
    component: GA4PageLocationAudit,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/fcr",
    component: GA4PropertyFCR,
    guard: { ...createGa4Guard(ga4AuditEnabled && automotiveFeaturesEnabled) },
  },
  {
    path: "/ga4-property/ccr",
    component: GA4PropertyCCR,
    guard: { ...createGa4Guard(ga4AuditEnabled && automotiveFeaturesEnabled) },
  },
  {
    path: "/ga4-property/hostname-audit",
    component: GA4HostnameAudit,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/hostname-audit/view",
    component: GA4HostnameDetails,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/channel-grouping-audit",
    component: GA4ChannelGroupingAudit,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/unassigned-channel-group",
    component: GA4UnassignedChannelGroup,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/self-referral-audit",
    component: GA4SelfReferralAudit,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/google-ads-audit",
    component: GA4GoogleAdsAudit,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/user-access",
    component: GA4UserAccessAudit,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-property/paid-media",
    guard: { ...requiresOrganizationGuard },
    render: () => <Navigate to="/paid-media" replace />,
  },
  {
    path: "/paid-media",
    component: PaidMedia,
    guard: { ...requiresOrganizationGuard, featureFlag: paidMediaEnabled },
  },
  {
    path: "/account-audits",
    component: AccountAudits,
    guard: { ...requiresOrganizationGuard, featureFlag: paidMediaEnabled },
  },
  {
    path: "/account-audits/:accountId",
    component: AccountAudits,
    guard: { ...requiresOrganizationGuard, featureFlag: paidMediaEnabled },
  },
  {
    path: "/paid-media/mappings",
    guard: { ...requiresOrganizationGuard },
    render: () => <Navigate to="/paid-media" replace />,
  },
  {
    path: "/ga4-template",
    component: GA4Template,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/ga4-access",
    component: GA4Access,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/organizations",
    guard: { ...requiresOrganizationGuard },
    render: ({ selectedOrgId }) =>
      selectedOrgId ? (
        <Navigate to={`/organizations/${selectedOrgId}`} replace />
      ) : (
        <Navigate to="/organizations/manage" replace />
      ),
  },
  {
    path: "/organizations/manage",
    component: ManageOrganizations,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/accounts",
    guard: { ...requiresOrganizationGuard },
    render: () => <Navigate to="/dashboard" replace />,
  },
  {
    path: "/accounts/manage",
    component: Accounts,
    guard: { ...createGa4Guard(ga4AuditEnabled) },
  },
  {
    path: "/accounts/:accountId",
    component: AccountRootRedirect,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/accounts/:accountId/:tab",
    component: ClientDetail,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/clients",
    component: ClientsCompatibility,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/clients/:accountId",
    component: LegacyClientRedirect,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/account",
    component: Account,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/organizations/:id/plan/checkout",
    component: OrganizationCheckout,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/organizations/:id/plan",
    component: OrganizationPlan,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/organizations/:id",
    component: OrganizationAdmin,
    guard: { ...requiresOrganizationGuard },
  },
  {
    path: "/admin",
    component: AdminPage,
    guard: { ...adminGuard },
  },
  {
    path: "/admin/tag-reference",
    component: GTMTagReferenceAdmin,
    guard: { ...adminGuard },
  },
  {
    path: "/admin/product-event-owners",
    component: ProductEventOwnerAdmin,
    guard: { ...adminGuard },
  },
  {
    path: "/theme-library",
    component: ThemeLibrary,
    guard: { ...createAdminGuard({ requiresOrg: false }) },
  },
  {
    path: "/ui-library",
    component: ComponentLibrary,
    guard: { ...createAdminGuard({ requiresOrg: false }) },
  },
]
